import { Button, EmptyState, ErrorState, HelmrMark, Icon, Skeleton, avatarTone, cx, formatTime, initials } from "@helmr/ui";
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { SKEW_WARN_MS, type KioskEngine, type KioskView, type ScanResult } from "./engine";
import { QrCode } from "./QrCode";
import { useCameraScanner, type Decoder, type Facing } from "./scanning";
import { strings } from "./strings";
import { memberBase, wallLink } from "./wall-link";
import { useWakeLock } from "./wake";
import s from "./kiosk.module.css";

export interface KioskScreenProps {
  engine: KioskEngine;
  now?: () => number;
  /** Staff button. Defaults to a hard navigation, so no staff state can leak into kiosk mode. */
  onStaff?: () => void;
  decoder?: Decoder;
}

function useView(engine: KioskEngine): KioskView {
  return useSyncExternalStore(engine.subscribe, engine.getState, engine.getState);
}

function useClock(now: () => number, everyMs = 5_000) {
  const [t, setT] = useState(now());
  useEffect(() => {
    setT(now());
    const id = setInterval(() => setT(now()), everyMs);
    return () => clearInterval(id);
  }, [now, everyMs]);
  return t;
}

export function KioskScreen({ engine, now = Date.now, onStaff, decoder }: KioskScreenProps) {
  const v = useView(engine);
  useWakeLock(v.phase !== "revoked");
  useEffect(() => { void engine.start(); return () => engine.stop(); }, [engine]);
  const t = useClock(now);

  if (v.phase === "revoked") return <Revoked />;
  return (
    <div className={s.root} data-mode={v.credential.mode} data-phase={v.phase}>
      <Chrome v={v} t={t} />
      {v.clockSkewMs !== null && Math.abs(v.clockSkewMs) > SKEW_WARN_MS ? (
        <div className={s.skew} role="alert">{strings.skewWarning(Math.round(Math.abs(v.clockSkewMs) / 60_000))}</div>
      ) : null}
      <main className={s.main}>
        {v.phase === "loading" ? <LoadingCard /> : v.phase === "needs_network" ? (
          <ErrorState title={strings.needsNetworkTitle} message={strings.needsNetworkBody} onRetry={() => void engine.refresh()} />
        ) : v.credential.mode === "scanner" ? (
          <ScannerBody engine={engine} v={v} decoder={decoder} />
        ) : (
          <WallBody v={v} />
        )}
      </main>
      <button type="button" className={s.staff} onClick={onStaff ?? (() => window.location.assign("/kiosk/exit"))}>
        <Icon name="lock" /> {strings.staffButton}
      </button>
    </div>
  );
}

/* ---------- chrome ---------- */

function Chrome({ v, t }: { v: KioskView; t: number }) {
  const tz = v.config?.branch.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const status = v.syncError ? "error" : v.online ? "online" : "offline";
  const label = status === "error" ? strings.statusSyncError : status === "online" ? strings.statusOnline : strings.statusOffline;
  const sub = [v.config?.branch.name, v.credential.label].filter(Boolean).join(" · ");
  return (
    <header className={s.band}>
      <div className={s.mark}><HelmrMark compact title="Helmr" /></div>
      <div className={s.gym}>
        <h1 title={v.credential.gymName}>{v.credential.gymName}</h1>
        {sub ? <p title={sub}>{sub}</p> : null}
      </div>
      <div className={s.status} data-status={status} role="status">
        <span className={s.dot} aria-hidden="true" />
        <span>{label}{v.queued > 0 ? ` · ${strings.queued(v.queued)}` : ""}</span>
      </div>
      <time className={s.clock} dateTime={new Date(t).toISOString()}>{formatTime(t, tz)}</time>
    </header>
  );
}

/* ---------- states ---------- */

function Revoked() {
  return (
    <div className={s.revoked} role="alert">
      <EmptyState icon="lock" title={strings.revokedTitle} description={strings.revokedBody}
        action={<Button onClick={() => window.location.assign("/signin")}>{strings.revokedStaff}</Button>} />
    </div>
  );
}

/** The skeleton of the result card while the config loads. */
function LoadingCard() {
  return (
    <div className={s.resultShell}>
      <div className={cx(s.result, s.skeletonCard)} aria-busy="true" aria-label={strings.loadingLabel}>
        <Skeleton width="30%" height="clamp(4rem, 12vw, 7rem)" style={{ borderRadius: "50%", aspectRatio: "1" }} />
        <Skeleton width="70%" height="clamp(1.75rem, 5vw, 3rem)" />
        <Skeleton width="45%" height="clamp(1.25rem, 3vw, 2rem)" />
      </div>
    </div>
  );
}

/* ---------- scanner ---------- */

function ScannerBody({ engine, v, decoder }: { engine: KioskEngine; v: KioskView; decoder?: Decoder }) {
  const video = useRef<HTMLVideoElement>(null);
  const [facing, setFacing] = useState<Facing>("environment");
  const onCode = useCallback((code: string) => void engine.scan(code), [engine]);
  const cam = useCameraScanner(video, { facing, enabled: true, onCode, decoder });
  const empty = v.rosterCount === 0;
  return (
    <div className={s.scanner}>
      <div className={s.viewport} data-problem={cam.problem ?? undefined}>
        <video ref={video} className={s.video} playsInline muted aria-label="Camera" />
        <div className={s.frame} aria-hidden="true" />
        {cam.ready && !v.result ? <p className={s.prompt}>{strings.scanPrompt}</p> : null}
        {cam.problem ? <CameraProblem problem={cam.problem} /> : null}
        <button type="button" className={s.switch} onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))} aria-label={strings.switchCamera}>
          <Icon name="swap" /> {strings.switchCamera}
        </button>
        {v.result ? <ResultCard r={v.result} /> : null}
      </div>
      {empty ? <div className={s.emptyNote}><EmptyState compact icon="users" title={strings.emptyRosterTitle} description={strings.emptyRosterBody} /></div> : null}
    </div>
  );
}

function CameraProblem({ problem }: { problem: NonNullable<ReturnType<typeof useCameraScanner>["problem"]> }) {
  const copy = {
    denied: { title: strings.cameraDeniedTitle, body: strings.cameraDeniedBody },
    none: { title: strings.cameraNoneTitle, body: strings.cameraNoneBody },
    unsupported: { title: strings.cameraUnsupportedTitle, body: strings.cameraUnsupportedBody },
    failed: { title: strings.cameraFailedTitle, body: strings.cameraFailedBody },
  }[problem];
  return (
    <div className={s.cameraProblem} role="alert">
      <EmptyState compact icon="alert" title={copy.title} description={copy.body} />
    </div>
  );
}

/** Photo or avatar, name, then the verdict in type readable from 2 m. Names wrap; they never push the verdict off screen. */
export function ResultCard({ r }: { r: ScanResult }) {
  const tone = avatarTone(r.name ?? "staff");
  return (
    <div className={s.resultShell}>
      <div className={cx(s.result, s[r.tone])} role="status" aria-live="assertive" data-tone={r.tone} data-testid="kiosk-result">
        <div className={s.who}>
          {r.photoUrl ? <img className={s.photo} src={r.photoUrl} alt="" /> : (
            <span className={cx(s.avatar, s[`t${tone}`])} aria-hidden="true">{r.name ? initials(r.name) : <Icon name="user" />}</span>
          )}
          {r.name ? <h2 className={s.name}>{r.name}</h2> : null}
        </div>
        <p className={s.headline}>{r.headline}</p>
        {r.notes.map((n) => <p key={n} className={s.note}>{n}</p>)}
        {r.queued ? <p className={s.queuedNote}>{strings.queuedNote}</p> : null}
      </div>
    </div>
  );
}

/* ---------- wall ---------- */

function WallBody({ v }: { v: KioskView }) {
  const seconds = v.config?.window.windowSeconds ?? 30;
  const base = memberBase(v.config);
  return (
    <div className={s.wall}>
      {v.wall && base ? (
        <>
          <QrCode className={s.wallCode} value={wallLink(base, v.wall.code)} label="Check-in code" />
          <p className={s.wallPrompt}>{strings.wallPrompt}</p>
          <p className={s.wallHint}>{`Refreshes every ${seconds} seconds`}</p>
        </>
      ) : v.wall ? (
        <EmptyState icon="alert" title={strings.wallNoHostTitle} description={strings.wallNoHostBody} />
      ) : v.wallProblem === "expired" ? (
        <EmptyState icon="alert" title={strings.wallExpiredTitle} description={strings.wallExpiredBody} />
      ) : v.wallProblem === "unavailable" ? (
        <EmptyState icon="alert" title={strings.wallUnavailableTitle} description={strings.wallUnavailableBody} />
      ) : (
        <Skeleton width="min(70vmin, 520px)" height="min(70vmin, 520px)" />
      )}
    </div>
  );
}
